import { parseFrame, type ProfilerFrame } from './profiler-model.js';

export interface ProfileSummaryRow {
  readonly name: string;
  readonly avgMs: number;
  readonly maxMs: number;
}

export interface ProfileSummary {
  readonly frames: number;
  readonly frameAvgMs: number;
  readonly frameMaxMs: number;
  readonly gpuAvgMs: number | null;
  readonly spikes: readonly { seq: number; ms: number }[];
  readonly cpu: readonly ProfileSummaryRow[];
  readonly gpu: readonly ProfileSummaryRow[];
  readonly counters: Readonly<Record<string, number>>;
}

type Totals = Map<string, { sum: number; max: number }>;

function rows(totals: Totals, frames: number, top: number): ProfileSummaryRow[] {
  return [...totals.entries()]
    .map(([name, t]) => ({ name, avgMs: t.sum / frames, maxMs: t.max }))
    .sort((a, b) => b.avgMs - a.avgMs)
    .slice(0, top);
}

function add(m: Totals, per: Map<string, number>): void {
  for (const [name, ms] of per) {
    const t = m.get(name) ?? { sum: 0, max: 0 };
    t.sum += ms;
    t.max = Math.max(t.max, ms);
    m.set(name, t);
  }
}

/** Averages recorded profiler frames (raw `profilerFrames` JSON) for the assistant: top scopes, GPU passes, counters, spikes. */
export function summarizeProfile(raw: readonly unknown[], top = 12): ProfileSummary | null {
  const frames = raw.map(parseFrame).filter((f): f is NonNullable<ReturnType<typeof parseFrame>> => f !== null);
  if (frames.length === 0) return null;
  const cpu: Totals = new Map();
  const gpu: Totals = new Map();
  const counters: Record<string, number> = {};
  let total = 0;
  let max = 0;
  let gpuSum = 0;
  let gpuFrames = 0;
  for (const f of frames) {
    total += f.ms;
    max = Math.max(max, f.ms);
    const perCpu = new Map<string, number>();
    for (const s of f.cpu) perCpu.set(s.name, (perCpu.get(s.name) ?? 0) + s.ms);
    add(cpu, perCpu);
    if (f.gpu) {
      const perGpu = new Map<string, number>();
      for (const p of f.gpu) perGpu.set(p.name, (perGpu.get(p.name) ?? 0) + p.ms);
      add(gpu, perGpu);
      gpuSum += f.gpu.reduce((a, p) => a + p.ms, 0);
      gpuFrames += 1;
    }
    for (const [k, v] of Object.entries(f.counters)) counters[k] = v;
  }
  const avg = total / frames.length;
  return {
    frames: frames.length,
    frameAvgMs: avg,
    frameMaxMs: max,
    gpuAvgMs: gpuFrames ? gpuSum / gpuFrames : null,
    spikes: frames
      .filter((f) => f.ms > Math.max(avg * 2, 20))
      .slice(0, 8)
      .map((f) => ({ seq: f.seq, ms: f.ms })),
    cpu: rows(cpu, frames.length, top),
    gpu: rows(gpu, gpuFrames || 1, top),
    counters,
  };
}

/** Chrome trace / Perfetto JSON (CPU on thread 1, GPU passes on thread 2, counters, spike markers). */
export function toChromeTrace(frames: readonly ProfilerFrame[], spikeMs = 33.4): string {
  const us = (ms: number) => ms * 1000;
  const events: Record<string, unknown>[] = [
    { ph: 'M', pid: 1, tid: 1, name: 'thread_name', args: { name: 'CPU' } },
    { ph: 'M', pid: 1, tid: 2, name: 'thread_name', args: { name: 'GPU' } },
  ];
  for (const f of frames) {
    events.push({ ph: 'X', pid: 1, tid: 1, name: `frame ${f.seq}`, ts: us(f.start), dur: us(f.ms) });
    if (f.ms > spikeMs) events.push({ ph: 'i', s: 'g', pid: 1, tid: 1, name: 'spike', ts: us(f.start), args: { ms: f.ms, frame: f.seq } });
    for (const s of f.cpu) events.push({ ph: 'X', pid: 1, tid: 1, name: s.name, ts: us(f.start + s.start), dur: us(s.ms) });
    for (const g of f.gpu ?? []) events.push({ ph: 'X', pid: 1, tid: 2, name: g.name, ts: us(f.start + g.start), dur: us(g.ms) });
    if (Object.keys(f.counters).length) events.push({ ph: 'C', pid: 1, name: 'counters', ts: us(f.start), args: f.counters });
  }
  return JSON.stringify({ traceEvents: events, displayTimeUnit: 'ms' });
}
